import React from "react"
import { Redirect, useLocation, useParams } from "react-router-dom"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { generatePath } from "~Shared/utils/routing.utils"
import { CompanyScopedRoutePathEnum, RoutePathEnum } from "./RoutePath.enum"

export const legacyRoutes: Array<{
    exact: boolean
    path: string
}> = [
    { exact: false, path: "/settings" },
    { exact: true, path: "/expenses" },
    { exact: true, path: "/expenses/:id" },
    { exact: true, path: "/expenses/:id/:childId" },
    { exact: true, path: "/subscriptions" },
    { exact: true, path: "/subscriptions/:id" },
    { exact: true, path: "/credit-cards" },
    { exact: true, path: "/credit-cards/:id" },
    { exact: true, path: "/transactions" },
    { exact: true, path: "/transactions/:id" },
    { exact: true, path: "/workflows" },
    { exact: true, path: "/workflows/:id" },
    { exact: true, path: "/employees" },
    { exact: true, path: "/employees/:id" },
    { exact: true, path: "/vendors" },
    { exact: true, path: "/vendors/:id" },
    { exact: true, path: "/costCenters" },
    { exact: true, path: "/costCenters/:id" },
    { exact: true, path: "/purchase-orders" },
    { exact: true, path: "/purchase-orders/:id" },
    { exact: true, path: "/delivery-notes" },
    { exact: true, path: "/delivery-notes/:id" },
    { exact: true, path: "/liquidity" },
    { exact: true, path: "/liquidity/dashboard" },
    { exact: true, path: "/liquidity/categorization" },
    { exact: true, path: "/liquidity/cashflow-planning" },
    { exact: true, path: "/reports" },
]

interface LegacyRouteRedirectProps {
    originalPath: (typeof legacyRoutes)[number]["path"]
}

export const LegacyRouteRedirect = ({ originalPath }: LegacyRouteRedirectProps) => {
    const { companyId } = useAuth()
    const { id, childId } = useParams<{ id?: string; childId?: string }>()
    const location = useLocation()
    if (!companyId) {
        return <Redirect to={{ pathname: generatePath(RoutePathEnum.LOGIN), state: { from: location.pathname } }} />
    }

    // Build resource path using generatePath with the original route pattern
    let resourcePath: string

    switch (originalPath) {
        // Expenses
        case "/expenses":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.EXPENSES, {})
            break
        case "/expenses/:id/:childId":
            if (!id || !childId) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.EXPENSES, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.FOLDER_EXPENSE_DETAIL, { id, childId })
            break
        case "/expenses/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.EXPENSES, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.EXPENSE_DETAIL, { id })
            break

        // Subscriptions
        case "/subscriptions":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.SUBSCRIPTIONS, {})
            break
        case "/subscriptions/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.SUBSCRIPTIONS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.SUBSCRIPTION_DETAIL, { id })
            break

        // Cards
        case "/credit-cards":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.CARDS, {})
            break
        case "/credit-cards/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.CARDS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.CARD_DETAIL, { id })
            break

        // Transactions
        case "/transactions":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.TRANSACTIONS, {})
            break
        case "/transactions/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.TRANSACTIONS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.TRANSACTION_DETAIL, { id })
            break

        // Workflows
        case "/workflows":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.WORKFLOWS, {})
            break
        case "/workflows/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.WORKFLOWS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.WORKFLOW_DETAIL, { id })
            break

        // Employees
        case "/employees":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.EMPLOYEES, {})
            break
        case "/employees/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.EMPLOYEES, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.EMPLOYEE_DETAIL, { id })
            break

        // Vendors
        case "/vendors":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.VENDORS, {})
            break
        case "/vendors/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.VENDORS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.VENDOR_DETAIL, { id })
            break

        // Cost Centers
        case "/costCenters":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.COST_CENTERS, {})
            break
        case "/costCenters/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.COST_CENTERS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.COST_CENTER_DETAIL, { id })
            break

        // Purchase Orders
        case "/purchase-orders":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDERS, {})
            break
        case "/purchase-orders/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDERS, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDER_DETAIL, { id })
            break

        // Delivery Notes
        case "/delivery-notes":
            resourcePath = generatePath(CompanyScopedRoutePathEnum.DELIVERY_NOTES, {})
            break
        case "/delivery-notes/:id":
            if (!id) {
                return <Redirect to={generatePath(CompanyScopedRoutePathEnum.DELIVERY_NOTES, {})} />
            }
            resourcePath = generatePath(CompanyScopedRoutePathEnum.DELIVERY_NOTE_DETAIL, { id })
            break

        // Settings
        case "/settings":
            // all the settings logic are consistent
            resourcePath = `/company/${companyId}${location.pathname}`
            break

        default:
            // Unknown legacy route
            return <Redirect to={RoutePathEnum.ROOT} />
    }

    // Preserve query string and hash if present
    const { search, hash } = location

    return <Redirect to={`${resourcePath}${search}${hash}`} />
}
